/*
 * Copyright (c) 2026 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import {Component, computed, signal} from '@angular/core';
import {ICellEditorAngularComp} from 'ag-grid-angular';
import {ICellEditorParams} from 'ag-grid-community';
import {EsciService} from '../../service/esci.service';

type CircuitSearchResult = {
  circNO: string;
  circSig: string;
  circSubsys: string;
  circColor: string;
  circDesc: string;
};

@Component({
  selector: 'app-icd-template-pin-cell-editor',
  template: `
    <div class="pin-editor-inline">
      @if (selectedCircuit()) {
        <p-tag
          class="selected-circuit-value"
          severity="info"
          [value]="selectedCircuit()">
        </p-tag>
      }
      <p-checkbox
        inputId="template-pin-x-checkbox"
        [ngModel]="xChecked()"
        (ngModelChange)="xChecked.set($event)"
        [binary]="true"
        (onChange)="onXCheckboxChange($event.checked)">
      </p-checkbox>
      <label class="x-label" for="template-pin-x-checkbox">X</label>

      <button
        type="button"
        pButton
        icon="pi pi-search"
        class="p-button-text action-button"
        pTooltip="Search circuit"
        (click)="openCircuitSearchModal()">
      </button>
    </div>

    <p-dialog
      header="Circuit Search"
      [visible]="searchModalVisible()"
      (visibleChange)="searchModalVisible.set($event)"
      [modal]="true"
      [draggable]="false"
      [resizable]="false"
      [focusOnShow]="false"
      [style]="{ width: '40rem' }"
      appendTo="body">
      <div class="pin-search-content">
        <div class="scope-note">Scope: GMW3176REV2</div>
        <div class="modal-help">{{ userMessage() }}</div>
        <div class="search-grid">
          <div>
            <label>Circuit Number</label>
            <input pInputText type="text" [(ngModel)]="searchCriteria.circNO" class="search-input"/>
          </div>
          <div>
            <label>Circuit Sig</label>
            <input pInputText type="text" [(ngModel)]="searchCriteria.circSig" class="search-input"/>
          </div>
          <div>
            <label>Subsystem</label>
            <input pInputText type="text" [(ngModel)]="searchCriteria.circSubsys" class="search-input"/>
          </div>
          <div>
            <label>Color</label>
            <input pInputText type="text" [(ngModel)]="searchCriteria.circColor" class="search-input"/>
          </div>
          <div class="full-width">
            <label>Description</label>
            <input pInputText type="text" [(ngModel)]="searchCriteria.circDesc" class="search-input"/>
          </div>
        </div>
        <div class="search-actions">
          <button type="button" pButton label="Search" (click)="searchCircuits()"></button>
          <button type="button" pButton label="Clear" class="p-button-secondary" (click)="clearSearch()"></button>
        </div>
        <p-table [value]="searchResults()" [tableStyle]="{ 'min-width': '100%' }" selectionMode="single"
                 [selection]="selectedSearchResult()"
                 (selectionChange)="selectedSearchResult.set($event)" dataKey="circNO">
          <ng-template pTemplate="header">
            <tr>
              <th>Circuit Number</th>
              <th>Circuit Sig</th>
              <th>Subsystem</th>
              <th>Color</th>
              <th>Description</th>
            </tr>
          </ng-template>
          <ng-template pTemplate="body" let-result>
            <tr [pSelectableRow]="result">
              <td>{{ result.circNO }}</td>
              <td>{{ result.circSig }}</td>
              <td>{{ result.circSubsys }}</td>
              <td>{{ result.circColor }}</td>
              <td>{{ result.circDesc }}</td>
            </tr>
          </ng-template>
        </p-table>
        <div class="results-footer">{{ resultSummary() }}</div>
      </div>
      <ng-template pTemplate="footer">
        <button type="button" pButton label="Select Circuit" [disabled]="!selectedSearchResult()"
                (click)="confirmCircuitSelection()"></button>
        <button type="button" pButton label="Cancel" class="p-button-text" (click)="closeCircuitSearchModal()"></button>
      </ng-template>
    </p-dialog>
  `,
  styles: [`
    :host { display: block; min-width: 0; width: 100%; }
    .pin-editor-inline { display: flex; align-items: center; gap: 0.35rem; min-width: 0; width: 100%; }
    .x-label { font-size: 0.82rem; margin-right: 0.2rem; }
    /*
     * The selected circuit renders as a PrimeNG p-tag inline with the controls
     * so the editor stays one row tall and fits inside the AG Grid cell's fixed
     * row height. The tag takes natural width (no flex-grow) so the X checkbox
     * and search magnifier sit immediately after it instead of being pushed to
     * the right edge of the cell.
     */
    .selected-circuit-value {
      flex: 0 1 auto;
      min-width: 0;
      max-width: 100%;
      overflow: hidden;
      display: inline-flex;
      font-size: 0.75rem;
    }
    .pin-search-content { display: grid; gap: 0.75rem; }
    .scope-note { font-size: 0.8rem; color: #666; font-weight: 600; }
    .modal-help { font-size: 0.82rem; color: #4b5563; }
    .search-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 0.6rem; }
    .search-grid label { display: block; font-size: 0.78rem; margin-bottom: 0.2rem; }
    .full-width { grid-column: 1 / -1; }
    .search-actions { display: flex; gap: 0.5rem; }
    .results-footer { font-size: 0.8rem; color: #374151; margin-top: 0.2rem; }
    .search-input { width: 100%; }
  `],
  standalone: false
})
export class IcdTemplatePinCellEditorComponent implements ICellEditorAngularComp {
  // Signal-backed state. The cell editor is hosted by AG Grid's framework-component
  // wrapper and the dialog uses `appendTo="body"`, so plain class fields mutated
  // from an RxJS `next` callback did not reliably re-render the dialog on the
  // first response (the table remained empty until a second Search click).
  // Signals propagate through Angular's reactive graph and update the dialog
  // bindings on the first response.
  readonly xChecked = signal(false);
  readonly selectedCircuit = signal('');
  readonly searchModalVisible = signal(false);
  readonly searchResults = signal<CircuitSearchResult[]>([]);
  readonly selectedSearchResult = signal<CircuitSearchResult | null>(null);
  readonly userMessage = signal("Please enter a search term or select 'Clear'.");
  readonly resultSummary = computed(() => {
    const selected = this.selectedSearchResult();
    if (selected?.circNO) {
      return `Selected Circuit: ${selected.circNO}`;
    }
    return `Matching Circuits: ${this.searchResults().length}`;
  });

  searchCriteria: { circNO: string; circSig: string; circSubsys: string; circColor: string; circDesc: string } = {
    circNO: '',
    circSig: '',
    circSubsys: '',
    circColor: '',
    circDesc: ''
  };
  width = 220;
  private params: ICellEditorParams | null = null;

  constructor(private readonly esciService: EsciService) {
  }

  agInit(params: ICellEditorParams): void {
    this.params = params;
    const normalizedValue = this.normalizeSelection(params.value);
    if (normalizedValue === 'X') {
      this.xChecked.set(true);
      this.selectedCircuit.set('');
    } else if (!normalizedValue) {
      this.xChecked.set(false);
      this.selectedCircuit.set('');
    } else {
      this.xChecked.set(false);
      this.selectedCircuit.set(normalizedValue);
    }
    this.width = params.colDef?.width || params.colDef?.maxWidth || 220;
  }

  getValue(): string {
    return this.selectedCircuit() || (this.xChecked() ? 'X' : '');
  }

  onXCheckboxChange(value: boolean): void {
    this.xChecked.set(!!value);
    this.selectedCircuit.set('');
    this.selectedSearchResult.set(null);
  }

  openCircuitSearchModal(): void {
    const current = this.selectedCircuit();
    this.selectedSearchResult.set(current ? {
      circNO: current,
      circSig: '',
      circSubsys: '',
      circColor: '',
      circDesc: ''
    } : null);
    this.userMessage.set("Please enter a search term or select 'Clear'.");
    // Do NOT auto-fire a circuit search here. Running searchCircuits() synchronously
    // while we also flip searchModalVisible true creates two problems:
    //   1. The async response can resolve before the dialog's first stability check
    //      and Angular throws NG0100 on `{{ userMessage }}` / `[value]="searchResults"`
    //      because those bindings were just set in this same change-detection cycle.
    //   2. The in-flight auto-search response can arrive after the user's first
    //      Search click and overwrite the filtered results, forcing a second click.
    // The existing helper text ("Please enter a search term or select 'Clear'.")
    // already instructs the user to drive the first search themselves.
    this.searchResults.set([]);
    this.searchModalVisible.set(true);
  }

  closeCircuitSearchModal(): void {
    this.searchModalVisible.set(false);
  }

  confirmCircuitSelection(): void {
    this.selectCircuitFromModal(this.selectedSearchResult()?.circNO || '');
    this.closeCircuitSearchModal();
    // Commit the cell edit so AG Grid swaps the editor UI (X checkbox + search icon
    // + selected value) for the normal cell renderer immediately. Without this the
    // editor stays open until the user clicks off the cell, which looks clunky.
    // Defer past the current click microtask so AG Grid finishes its own event
    // handling before we tear the editor down.
    const api = this.params?.api;
    if (api) {
      setTimeout(() => api.stopEditing(false), 0);
    }
  }

  selectCircuitFromModal(circuit: string): void {
    const normalizedCircuit = this.normalizeSelection(circuit);
    if (!normalizedCircuit || normalizedCircuit === 'X') {
      return;
    }
    this.selectedCircuit.set(normalizedCircuit);
    this.xChecked.set(false);
  }

  searchCircuits(): void {
    this.esciService.getIcdCircuitSearchResults({
      circNO: this.searchCriteria.circNO,
      circSig: this.searchCriteria.circSig,
      circSubsys: this.searchCriteria.circSubsys,
      circColor: this.searchCriteria.circColor,
      circDesc: this.searchCriteria.circDesc,
      limit: 100
    }).subscribe({
      next: rows => {
        const list = rows || [];
        this.searchResults.set(list);
        this.userMessage.set(list.length ? '' : 'No results found.');
        const current = this.selectedCircuit();
        if (current) {
          this.selectedSearchResult.set(
            list.find(r => this.normalizeSelection(r?.circNO) === current) || null
          );
        }
      },
      error: () => {
        this.searchResults.set([]);
        this.selectedSearchResult.set(null);
        this.userMessage.set('FAILED TO RETRIEVE DATA');
      }
    });
  }

  clearSearch(): void {
    // Mirrors `openCircuitSearchModal()`'s no-auto-search policy. Setting
    // `userMessage` and then synchronously calling `searchCircuits()` lets the HTTP
    // `next` callback overwrite `userMessage` to '' (when results return) inside the
    // dialog's first change-detection cycle, throwing NG0100 on `{{ userMessage }}`.
    // It is also contradictory UX: the helper text instructs the user to enter
    // criteria while we would already be issuing an empty search on their behalf.
    // Reset the form state and let the user drive the next Search click themselves.
    this.searchCriteria = {
      circNO: '',
      circSig: '',
      circSubsys: '',
      circColor: '',
      circDesc: ''
    };
    this.searchResults.set([]);
    this.selectedSearchResult.set(null);
    this.userMessage.set("Please enter a search term or select 'Clear'.");
  }

  private normalizeSelection(value: unknown): string {
    if (value == null) {
      return '';
    }
    const normalized = String(value).trim();
    return normalized.toUpperCase() === 'X' ? 'X' : normalized;
  }
}
